// Haptics, where a phone can (DESIGN.md, Views, "Haptics, where a phone can"): a short tick under the
// finger for a few moments (the deck's swipe crossing the point it will count, an answer and Undo, a
// to-do ticked done), and nothing anywhere else. Only from a finger (`pointerType` "touch"): never from a
// mouse, a pen or the keys, so a computer never buzzes, a touch screen laptop included only as touched.
//
// Through the web's vibration (`navigator.vibrate`), which Android's browsers have. An iPhone has none,
// and gets nothing: Safari's on/off switch (`<input type="checkbox" switch>`) ticks when a person flips
// it, and a hidden one flipped from script (`label.click()`) used to tick too, but WebKit made that click
// untrusted on purpose (bug 309082, "Haptic feedback for <input type=checkbox switch> can be triggered
// with a programmatic click on an associated label", landed 2026-05-20; iOS 26.5 on), and a switch ticks
// only for a trusted click within a user gesture (CheckboxInputType.cpp). So there's no script-made tick
// on a current iPhone to reach for here.

// The moments: a light tick as a swipe crosses the point where letting go will count, and a firmer one as
// something is done (an answer, Undo, a to-do ticked done). In ms of vibration: under about 8 ms many
// phones' motors don't spin up at all, and over about 20 it reads as a buzz, not a tick.
export const MOMENTS = { tick: 10, commit: 16 } as const;
export type Moment = keyof typeof MOMENTS;

// What a moment does, from what made it and what the browser has: vibrate, or nothing.
export function hapticWay(pointerType: string | undefined, canVibrate: boolean): "vibrate" | "none" {
  return pointerType === "touch" && canVibrate ? "vibrate" : "none";
}

// A moment, answering the event that made it (a pointer event, or a click, which is one where the
// browser says what made it; a click from the keys says "" and gets nothing).
// Typed without the browser's own types, so the tests outside the browser can import it.
export function haptic(moment: Moment, event: { nativeEvent: object }): void {
  const { pointerType } = event.nativeEvent as { pointerType?: unknown };
  const nav = globalThis.navigator as { vibrate?: (ms: number) => boolean } | undefined;
  const way = hapticWay(typeof pointerType === "string" ? pointerType : undefined, typeof nav?.vibrate === "function");
  if (way === "vibrate") nav?.vibrate?.(MOMENTS[moment]);
}
